/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Safari / WebKit (including every iOS browser, which uses WKWebView) did not
 * implement `ReadableStream.prototype[Symbol.asyncIterator]` until Safari 26.4.
 * Apollo's TanStack Start integration hydrates the SSR transport with
 * `for await (const event of stream)`, which throws
 * `TypeError: undefined is not a function (near '...t of e...')` without this.
 *
 * @see https://caniuse.com/mdn-api_readablestream_--asynciterator
 * @see https://streams.spec.whatwg.org/#rs-asynciterator
 */

type ReadableStreamIteratorOptions = {
  preventCancel?: boolean
}

export function installReadableStreamAsyncIteratorPolyfill(): boolean {
  if (typeof ReadableStream === 'undefined') {
    return false
  }

  const proto = ReadableStream.prototype
  if (typeof proto[Symbol.asyncIterator] === 'function') {
    return false
  }

  const values = function values(
    this: ReadableStream,
    options?: ReadableStreamIteratorOptions
  ): AsyncIterableIterator<unknown> {
    const preventCancel = options?.preventCancel ?? false
    const reader = this.getReader()
    const iterator: AsyncIterableIterator<unknown> = {
      async next(): Promise<IteratorResult<unknown>> {
        try {
          const result = await reader.read()
          if (result.done) {
            reader.releaseLock()
            return { done: true, value: result.value }
          }
          return { done: false, value: result.value }
        } catch (error) {
          reader.releaseLock()
          throw error
        }
      },
      async return(value?: unknown) {
        if (preventCancel) {
          reader.releaseLock()
        } else {
          const cancelPromise = reader.cancel(value)
          reader.releaseLock()
          await cancelPromise
        }
        return { done: true, value }
      },
      [Symbol.asyncIterator]() {
        return iterator
      },
    }
    return iterator
  }

  Object.defineProperty(proto, 'values', {
    configurable: true,
    enumerable: false,
    writable: true,
    value: values,
  })
  Object.defineProperty(proto, Symbol.asyncIterator, {
    configurable: true,
    enumerable: false,
    writable: true,
    value: values,
  })

  return true
}

installReadableStreamAsyncIteratorPolyfill()
